// Point d'entrée du Task Pane : attend qu'Office soit prêt puis monte React.
import { createRoot, Root } from "react-dom/client";
import { createElement } from "react";
import App from "./App";
import { ErrorBoundary } from "./components/ErrorBoundary";

let root: Root | null = null;
let bootCompleted = false;

const OFFICE_READY_TIMEOUT_MS = 20000;

// Affiche un message d'erreur lisible si quelque chose se passe mal AVANT que React ne mount.
function showFatalError(message: string, detail?: string): void {
  bootCompleted = true;
  const container = document.getElementById("root");
  if (!container) return;
  container.innerHTML =
    '<div style="padding:16px;color:#a4262c;font-size:13px;font-family:Segoe UI,sans-serif;line-height:1.5">' +
    '<h3 style="margin-top:0">Demarrage impossible</h3>' +
    "<p><strong>" +
    escapeHtml(message) +
    "</strong></p>" +
    (detail ? '<pre style="font-size:11px;white-space:pre-wrap;color:#605e5c">' + escapeHtml(detail) + "</pre>" : "") +
    '<button onclick="window.location.reload()" style="margin-top:8px;padding:6px 12px;background:#0078d4;color:white;border:none;border-radius:4px;cursor:pointer">Recharger</button>' +
    "</div>";
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function mountApp(info: { host: Office.HostType; platform: Office.PlatformType }): void {
  if (bootCompleted) return;
  bootCompleted = true;

  // eslint-disable-next-line no-console
  console.info("[GLPI Helper] Office.onReady", {
    host: info.host,
    platform: info.platform,
  });

  if (info.host !== Office.HostType.Outlook) {
    showFatalError("Ce add-in fonctionne uniquement dans Outlook.", `Host detecte : ${info.host}`);
    return;
  }

  const container = document.getElementById("root");
  if (!container) {
    // eslint-disable-next-line no-console
    console.error("[GLPI Helper] container #root introuvable dans le DOM");
    return;
  }
  container.innerHTML = "";
  root = createRoot(container);
  root.render(createElement(ErrorBoundary, null, createElement(App)));
}

// Capture les erreurs JS globales (avant et pendant le mount React).
window.addEventListener("error", (event) => {
  // eslint-disable-next-line no-console
  console.error("[GLPI Helper] window.error", event.error || event.message);
  if (!bootCompleted) {
    showFatalError(
      "Erreur JavaScript au chargement.",
      String(event.error?.message || event.message || event.error)
    );
  }
});
window.addEventListener("unhandledrejection", (event) => {
  // eslint-disable-next-line no-console
  console.error("[GLPI Helper] unhandledrejection", event.reason);
});

// Timeout : si Office.onReady ne repond pas (souvent certificat localhost non accepte sur Outlook web).
window.setTimeout(() => {
  if (bootCompleted) return;
  const onWeb =
    typeof window !== "undefined" &&
    (window.location.hostname.includes("office.com") ||
      window.location.hostname.includes("office365.com") ||
      window.parent !== window);
  showFatalError(
    "Office.js ne repond pas (timeout).",
    onWeb
      ? "Outlook web : ouvrez https://localhost:3000/taskpane.html dans un nouvel onglet du navigateur, " +
          "acceptez le certificat de developpement si demande, puis rechargez Outlook (Ctrl+F5). " +
          "Verifiez aussi que npm run dev-server tourne."
      : "Verifiez que le serveur de dev tourne (npm start) et rechargez le task pane."
  );
}, OFFICE_READY_TIMEOUT_MS);

// Office.js absent = script bloque ou URL incorrecte.
if (typeof Office === "undefined") {
  showFatalError(
    "Office.js n'a pas ete charge.",
    "Verifiez votre connexion et que https://appsforoffice.microsoft.com n'est pas bloque. " +
      "Sur Outlook web, acceptez aussi le certificat https://localhost:3000 dans le navigateur."
  );
} else {
  try {
    Office.onReady((info) => {
      try {
        mountApp(info);
      } catch (e) {
        const err = e as Error;
        // eslint-disable-next-line no-console
        console.error("[GLPI Helper] echec mount React", err);
        showFatalError(err.message || "Erreur inconnue au mount", err.stack);
      }
    });
  } catch (e) {
    const err = e as Error;
    // eslint-disable-next-line no-console
    console.error("[GLPI Helper] echec Office.onReady", err);
    showFatalError(err.message || "Office.onReady a echoue", err.stack);
  }
}
